/**
 * One spelling of an amount for the whole app.
 *
 * The collab briefs, the display-rights composer and the companion cards all
 * render integer cents under a currency code that upstream data decides, so
 * they share this helper rather than each keeping an `Intl.NumberFormat` of
 * their own. What they do not share is the rounding and the words to fall back
 * on when a code is unknown, and those are the two options below.
 */

/**
 * Symbols cover more currencies than a brief may be paid in: the display-rights
 * composer formats amounts the brand agreed elsewhere. What a brief can promise
 * is `COLLAB_SETTLEABLE_CURRENCIES` in the shared contract, read straight from
 * there by the compensation field.
 */
const CURRENCY_SYMBOL: Record<string, string> = {
  EUR: '€',
  USD: '$',
  GBP: '£',
};

export const currencySymbol = (currency: string): string => CURRENCY_SYMBOL[currency] ?? currency;

export type FormatCentsOptions = {
  /** Fixed ceiling on the decimals. Left out: none on a whole amount, two otherwise. */
  maximumFractionDigits?: number;
  /** What to show when Intl refuses the currency code. `amount` is in units, not cents. */
  fallback?: (amount: number, currency: string) => string;
};

/**
 * Format an integer cents amount as a localized currency string (no decimals
 * when the amount is whole, matching the design's "1 000 €"). Falls back to a
 * plain symbol join if Intl throws on an unknown currency code.
 */
export const formatCents = (
  amountCents: number,
  currency: string,
  locale: string,
  options: FormatCentsOptions = {}
): string => {
  const amount = amountCents / 100;
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      maximumFractionDigits: options.maximumFractionDigits ?? (Number.isInteger(amount) ? 0 : 2),
    }).format(amount);
  } catch {
    return options.fallback
      ? options.fallback(amount, currency)
      : `${amount} ${currencySymbol(currency)}`;
  }
};
